// utils/dateHelper.ts
import { defaultLocale, Locale } from '@/components/language/config';
import LocalStorage from '@/store/LocalStorage';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/it';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export type DateType = dayjs.Dayjs;
dayjs.extend(localeData);
dayjs.extend(weekday);

// Imposta il locale predefinito (puoi cambiarlo come preferisci)
dayjs.locale('it');

export const isValidDate = (date: string): boolean => {
  return dayjs(date).isValid();
};
export const createDate = (
  date: string,
  format: any = dateFormats[defaultLocale],
): dayjs.Dayjs => {
  return dayjs(date, format);
};

export const today = (): dayjs.Dayjs => {
  return dayjs();
};
export const FormatSendDate = (
  date: string,
  format = dateFormats.en,
): string => {
  return dayjs(date).format('YYYY-MM-DD');
};
export const dateFormats: Partial<Record<Locale, string>> = {
  en: 'MMMM DD, YYYY', // formato inglese
  it: 'DD MMM YYYY', // formato italiano
};
export const dateTimeFormats: Partial<Record<Locale, string>> = {
  en: 'MMMM DD, YYYY HH:mm:ss', // formato inglese
  it: 'DD MMM YYYY HH:mm:ss', // formato italiano
};

export const getCurrentLocaleFormat = (
  locale: Locale = getLanguageFromLocalStorage(),
  showTime: boolean = false,
): string => {
  let cuurentLocale = '';
  if (showTime) {
    cuurentLocale = (dateTimeFormats[locale] ||
      dateTimeFormats[defaultLocale] ||
      dateFormats.en) as string;
  } else {
    cuurentLocale = (dateFormats[locale] ||
      dateFormats[defaultLocale] ||
      dateFormats.en) as string;
  }
  //console.log('🚀 ~ getCurrentLocaleFormat ~ cuurentLocale:', cuurentLocale);
  return cuurentLocale;
};
export const getLanguageFromLocalStorage = (): Locale => {
  return LocalStorage.getItem('i18nextLng') || defaultLocale;
};
export const formatDate = (
  date: dayjs.ConfigType,
  format: string = 'YYYY-MM-DD',
): string => {
  return dayjs(date).format(format);
};

export const formatFullDate = (
  date: dayjs.ConfigType,
  locale: Locale = getLanguageFromLocalStorage(),
): string => {
  const format = dateFormats[locale] || dateFormats[defaultLocale];
  return dayjs(date).format(format); // Formatta la data per includere il giorno della settimana
};

export const getCurrentDate = (
  locale = getLanguageFromLocalStorage(),
): string => {
  const format = dateFormats[locale] || dateFormats[defaultLocale];
  return dayjs().format(format);
};

export const getCurrentDateTime = (): string => {
  return dayjs().format('YYYY-MM-DD HH:mm:ss');
};

export const getCurrentFullDate = (): string => {
  return dayjs().format('dddd, YYYY-MM-DD'); // Includi il giorno della settimana
};

export const getWeekday = (): string => {
  return dayjs().format('dddd');
};

export const setLocale = (locale: string): void => {
  dayjs.locale(locale);
};

export const parseDate = (
  dateString: string,
  format: string = 'YYYY-MM-DD',
): dayjs.Dayjs => {
  return dayjs(dateString, format);
};

// Dichiarazioni di overload
export function dateFromTSunix(
  date: number,
  formatted: boolean,
  showTime: boolean,
): string;
export function dateFromTSunix(date: number, formatted: boolean): string;
export function dateFromTSunix(date: number): DateType;
// Implementazione
export function dateFromTSunix(
  date: number,
  formatted?: boolean,
  showTime?: boolean,
): string | DateType {
  const dateDays = dayjs.unix(date);
  //console.log('🚀 ~ dateDays:', dateDays);
  const localeFormat = getCurrentLocaleFormat(undefined, showTime);
  //console.log('🚀 ~ localeFormat:', localeFormat);
  if (showTime) {
    return formatted ? dateDays.format(localeFormat) : dateDays;
  }
  return formatted ? dateDays.format(localeFormat) : dateDays;
}

export function convertAndFormatDate(
  dateString: string,
  outputFormat: string = getCurrentLocaleFormat(undefined, true),
): string {
  // Parse the input string to a Day.js object
  const date = dayjs(dateString);

  // Format the date according to the specified output format
  return date.format(outputFormat);
}

export const getDateFromSeconds = (seconds: number): string => {
  return dateFromTSunix(dayjs().add(seconds, 'seconds').unix(), true, true);
};
